/**
 * 지표별 설명 가이드 (KO/EN).
 *
 * 본 앱은 의료기기가 아니며, 모든 측정값은 웰니스·연구 참고용입니다.
 * This app is not a medical device. All measurements are for wellness and research reference only.
 *
 * 따라서 가이드 문구는 질환명·의료 권고·건강 판정을 포함하지 않고,
 * 측정 수치·정상 범위·공식·측정 방법 등 사실만 기술한다.
 */

/** 모든 지표 가이드 하단에 붙는 면책 문구 */
export const INDEX_GUIDE_DISCLAIMER_KO =
  '<strong>참고:</strong> 본 앱은 의료기기가 아니며, 모든 측정값은 웰니스·연구 참고용입니다.';

/** 영문 면책 문구 */
export const INDEX_GUIDE_DISCLAIMER_EN =
  '<strong>Note:</strong> This app is not a medical device. All measurements are for wellness and research reference only.';

export const indexGuidesKO: Record<string, string> = {
  // EEG 지수
  '집중력': `
    <strong>설명:</strong> 집중력 지수는 인지적 집중 수준을 나타내며, 베타파 파워와 알파파 및 세타파 파워의 합의 비율로 계산됩니다. 높은 값은 깊은 집중을, 낮은 값은 주의 분산을 의미합니다.<br/>
    <strong>공식:</strong> 집중력 지수 = 베타파 파워 / (알파파 파워 + 세타파 파워)<br/>
    <strong>정상 범위:</strong> 1.8 - 2.4<br/>
    <strong>해석:</strong><br/>
    • 1.8 - 2.4: 정상적인 집중 수준<br/>
    • 1.8 미만: 주의력 결핍 혹은 졸음<br/>
    • 2.4 이상: 과도한 집중 혹은 스트레스<br/>
    <strong>참고문헌:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance. Brain Research Reviews, 29(2-3), 169-195
  `,
  '이완도': `
    <strong>설명:</strong> 이완도 지수는 상대적 알파파 활동을 기반으로 정신적 이완 상태를 측정합니다. 높은 값일수록 더 이완된 상태를 나타냅니다.<br/>
    <strong>공식:</strong> 이완도 지수 = 알파파 파워 / (알파파 파워 + 베타파 파워)<br/>
    <strong>정상 범위:</strong> 0.18 - 0.22 (정상적인 긴장 상태)<br/>
    <strong>해석:</strong> 0.18 미만: 긴장 및 스트레스 상태; 0.22 초과: 과도한 이완<br/>
    <strong>참고문헌:</strong> Bazanova, O. M., & Vernon, D. (2014). Neuroscience & Biobehavioral Reviews, 44, 94-110
  `,
  '스트레스': `
    <strong>설명:</strong> 스트레스 지수는 정신적 스트레스와 각성 상태를 나타내며, 고주파수(베타파, 감마파) 활동 증가와 함께 상승합니다.<br/>
    <strong>공식:</strong> 스트레스 지수 = (베타파 파워 + 감마파 파워) / (알파파 파워 + 세타파 파워)<br/>
    <strong>정상 범위:</strong> 3.0 - 4.0 (정상 범위)<br/>
    <strong>해석:</strong> 2.0 미만: 매우 낮은 스트레스; 2.0-3.0: 낮은 스트레스; 3.0-4.0: 정상 범위; 4.0-5.0: 높은 스트레스; 5.0 초과: 매우 높은 스트레스 수치<br/>
    <strong>참고문헌:</strong> Ahn, J. W., et al. (2019). Sensors, 19(21), 4644
  `,
  '좌우뇌 균형': `
    <strong>설명:</strong> 좌우뇌 균형은 좌뇌와 우뇌 간 알파파 활동의 균형을 나타내며, 감정적 및 인지적 편향을 반영합니다.<br/>
    <strong>공식:</strong> (좌뇌 알파파 - 우뇌 알파파) / (좌뇌 알파파 + 우뇌 알파파)<br/>
    <strong>정상 범위:</strong> -0.1 ~ 0.1 (균형 상태)<br/>
    <strong>해석:</strong> -0.1 이하: 창의적 (우뇌 우세); 0.1 이상: 논리적 (좌뇌 우세)<br/>
    <strong>참고문헌:</strong> Davidson, R. J. (2004). Biological Psychology, 67(1-2), 219-234
  `,
  '인지 부하': `
    <strong>설명:</strong> 인지 부하는 세타파/알파파 비율을 기반으로 정신적 작업 부하와 노력을 반영합니다.<br/>
    <strong>공식:</strong> 인지 부하 = 세타파 파워 / 알파파 파워<br/>
    <strong>정상 범위:</strong> 0.3 - 0.8 (최적 부하)<br/>
    <strong>해석:</strong> 0.3 미만: 낮은 참여도; 0.8 초과: 높은 인지 부하; 1.2 초과: 과부하<br/>
    <strong>참고문헌:</strong> Gevins, A., & Smith, M. E. (2003). Theoretical Issues in Ergonomics Science, 4(1-2), 113-131
  `,
  '정서 안정성': `
    <strong>설명:</strong> 정서 안정성은 낮은 주파수와 감마파 파워의 비율을 기반으로 감정 조절 능력을 측정합니다.<br/>
    <strong>공식:</strong> 정서 안정성 = (알파파 파워 + 세타파 파워) / 감마파 파워<br/>
    <strong>정상 범위:</strong> 0.4 - 0.8 (정상 범위)<br/>
    <strong>해석:</strong> 0.4 미만: 정서 불안정, 과도한 각성; 0.8 초과: 정서 둔화, 과도한 억제<br/>
    <strong>참고문헌:</strong> Knyazev, G. G. (2007). Neuroscience & Biobehavioral Reviews, 31(3), 377-395
  `,
  '총 파워': `
    <strong>신경활동성 (Neural Activity)</strong><br/>
    <strong>설명:</strong> 모든 EEG 밴드 파워의 합으로, 전체적인 신경 활동 수준을 나타냅니다.<br/>
    <strong>공식:</strong> 델타파 + 세타파 + 알파파 + 베타파 + 감마파 밴드 파워의 합<br/>
    <strong>정상 범위:</strong> 850-1150 μV²<br/>
    <strong>해석:</strong><br/>
    • 850-1150: 정상적인 신경 활동 수준<br/>
    • 1150 이상: 과도한 신경 활동 (과각성, 스트레스 상태)<br/>
    • 850 미만: 억제된 신경 활동 (저각성, 졸음 상태)<br/>
    <strong>참고문헌:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance
  `,
  
  '신경활동성': `
    <strong>신경활동성 (Neural Activity)</strong><br/>
    <strong>설명:</strong> 모든 EEG 밴드 파워의 합으로, 전체적인 신경 활동 수준을 나타냅니다.<br/>
    <strong>공식:</strong> 델타파 + 세타파 + 알파파 + 베타파 + 감마파 밴드 파워의 합<br/>
    <strong>정상 범위:</strong> 850-1150 μV²<br/>
    <strong>해석:</strong><br/>
    • 850-1150: 정상적인 신경 활동 수준<br/>
    • 1150 이상: 과도한 신경 활동 (과각성, 스트레스 상태)<br/>
    • 850 미만: 억제된 신경 활동 (저각성, 졸음 상태)<br/>
    <strong>참고문헌:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance
  `,

  // 새로운 라벨명에 대한 설명 추가
  '집중력 (Focus)': `
    <strong>설명:</strong> 집중력 지수는 인지적 집중 수준을 나타내며, 베타파 파워와 알파파 및 세타파 파워의 합의 비율로 계산됩니다. 높은 값은 깊은 집중을, 낮은 값은 주의 분산을 의미합니다.<br/>
    <strong>공식:</strong> 집중력 지수 = 베타파 파워 / (알파파 파워 + 세타파 파워)<br/>
    <strong>정상 범위:</strong> 1.8 - 2.4<br/>
    <strong>해석:</strong><br/>
    • 1.8 - 2.4: 정상적인 집중 수준<br/>
    • 1.8 미만: 주의력 결핍 혹은 졸음<br/>
    • 2.4 이상: 과도한 집중 혹은 스트레스<br/>
    <strong>참고문헌:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance. Brain Research Reviews, 29(2-3), 169-195
  `,
  
  '이완및긴장도 (Arousal)': `
    <strong>설명:</strong> 이완 및 긴장도 지수는 상대적 알파파 활동을 기반으로 정신적 각성과 이완 상태를 측정합니다. 높은 값일수록 더 이완된 상태를, 낮은 값일수록 긴장된 상태를 나타냅니다.<br/>
    <strong>공식:</strong> 이완도 지수 = 알파파 파워 / (알파파 파워 + 베타파 파워)<br/>
    <strong>정상 범위:</strong> 0.18 - 0.22 (정상적인 긴장 상태)<br/>
    <strong>해석:</strong> 0.18 미만: 긴장 및 스트레스 상태; 0.22 초과: 과도한 이완<br/>
    <strong>참고문헌:</strong> Bazanova, O. M., & Vernon, D. (2014). Neuroscience & Biobehavioral Reviews, 44, 94-110
  `,
  
  '정서안정성 (Valence)': `
    <strong>설명:</strong> 정서안정성 지수는 낮은 주파수와 감마파 파워의 비율을 기반으로 감정 조절 능력과 정서적 안정성을 측정합니다. 적절한 범위 내의 값일수록 안정된 정서 상태를 나타냅니다.<br/>
    <strong>공식:</strong> 정서 안정성 = (알파파 파워 + 세타파 파워) / 감마파 파워<br/>
    <strong>정상 범위:</strong> 0.4 - 0.8 (정상 범위)<br/>
    <strong>해석:</strong> 0.4 미만: 정서 불안정, 과도한 각성; 0.8 초과: 정서 둔화, 과도한 억제<br/>
    <strong>참고문헌:</strong> Knyazev, G. G. (2007). Neuroscience & Biobehavioral Reviews, 31(3), 377-395
  `,
  
  '스트레스 (Stress)': `
    <strong>설명:</strong> 스트레스 지수는 정신적 스트레스와 각성 상태를 나타내며, 고주파수(베타파, 감마파) 활동 증가와 함께 상승합니다. 높은 값일수록 더 높은 스트레스 수준을 의미합니다.<br/>
    <strong>공식:</strong> 스트레스 지수 = (베타파 파워 + 감마파 파워) / (알파파 파워 + 세타파 파워)<br/>
    <strong>정상 범위:</strong> 3.0 - 4.0 (정상 범위)<br/>
    <strong>해석:</strong> 2.0 미만: 매우 낮은 스트레스; 2.0-3.0: 낮은 스트레스; 3.0-4.0: 정상 범위; 4.0-5.0: 높은 스트레스; 5.0 초과: 매우 높은 스트레스 수치<br/>
    <strong>참고문헌:</strong> Ahn, J. W., et al. (2019). Sensors, 19(21), 4644
  `,

  // PPG 지수
  'BPM': `
    <strong>BPM (Beats Per Minute)</strong><br/>
    심박수 - 1분당 심장 박동 횟수<br/><br/>
    
    <strong>측정 방법:</strong> PPG 신호에서 피크 간격을 분석하여 계산<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 60-100 BPM: 정상 범위<br/>
    • 60 BPM 미만: 낮은 심박수<br/>
    • 100 BPM 초과: 높은 심박수<br/><br/>
    
    <strong>해석:</strong> 분당 심박 횟수 수치. 운동, 자세, 활동 상태 등에 따라 변동<br/><br/>
    
    <strong>참고문헌:</strong> American Heart Association Guidelines
  `,
  'SpO2': `
    <strong>SpO2 (Oxygen Saturation)</strong><br/>
    산소포화도 - 혈액 내 산소 농도<br/><br/>
    
    <strong>측정 방법:</strong> Red/IR 광흡수 비율을 이용한 Beer-Lambert 법칙 기반 계산<br/>
    • R = (Red_AC/Red_DC) / (IR_AC/IR_DC)<br/>
    • 다단계 보정 공식으로 SpO2 추정<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 98-100%: 정상 산소포화도<br/>
    • 95-98%: 정상 범위 (하한)<br/>
    • 90-95%: 산소포화도 다소 낮음<br/>
    • 90% 미만: 산소포화도 낮음 (측정값)<br/><br/>
    
    <strong>해석:</strong> 광학 센서로 추정한 산소포화도 수치 표시. 의료기기가 아니며 정확도 제한적<br/><br/>
    
    <strong>참고문헌:</strong> Pulse Oximetry Principles, IEEE TBME
  `,
  'Stress': `
    <strong>Stress Index (스트레스 지수)</strong><br/>
    HRV 기반 정규화된 스트레스 수준 (0.0-1.0)<br/><br/>
    
    <strong>공식:</strong> 정규화된 SDNN, RMSSD, 심박수의 가중평균<br/>
    • Stress = (정규화SDNN × 0.4) + (정규화RMSSD × 0.4) + (심박수스트레스 × 0.2)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 0.30-0.70: 정상 범위 (균형 상태)<br/>
    • 0.00-0.30: 매우 낮은 스트레스 (과도한 이완)<br/>
    • 0.70-0.90: 높은 스트레스 (긴장 상태)<br/>
    • 0.90-1.00: 매우 높은 스트레스 수치<br/><br/>
    
    <strong>해석:</strong> 낮음: 낮은 스트레스 수치; 높음: 높은 스트레스 수치<br/><br/>
    
    <strong>참고문헌:</strong> HRV Analysis Methods, Frontiers in Physiology
  `,
  'SDNN': `
    <strong>SDNN (Standard Deviation of NN intervals)</strong><br/>
    NN간격의 표준편차 - 전체 HRV 수준<br/><br/>
    
    <strong>공식:</strong> SDNN = √(Σ(RRᵢ - RR̄)² / (N-1))<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 30-100 ms: 정상 범위<br/>
    • 30 ms 미만: 심박 리듬 일정함 (스트레스나 피로 상태)<br/>
    • 100 ms 초과: 심박 변동 큼<br/><br/>
    
    <strong>해석:</strong> 낮음: 심박 변동 적음; 높음: 심박 변동 큼<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996
  `,
  'RMSSD': `
    <strong>RMSSD (Root Mean Square of Successive Differences)</strong><br/>
    연속 RR간격 차이의 제곱근 평균 제곱<br/><br/>
    
    <strong>공식:</strong> RMSSD = √(Σ(RRᵢ₊₁ - RRᵢ)² / (N-1))<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 20-50 ms: 정상 범위<br/>
    • 20 ms 미만: 긴장 상태 (휴식이 필요한 상태)<br/>
    • 50 ms 초과: 매우 편안한 상태 (깊은 휴식 상태)<br/><br/>
    
    <strong>해석:</strong> 낮음: 긴장 상태 (휴식 부족); 높음: 매우 편안한 상태<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996
  `,
  'PNN50': `
    <strong>PNN50 (Percentage of NN50)</strong><br/>
    50ms 초과 차이나는 연속 NN간격의 백분율<br/><br/>
    
    <strong>공식:</strong> PNN50 = (NN50 count / Total NN intervals) × 100%<br/>
    NN50 = |RRᵢ₊₁ - RRᵢ| > 50ms인 간격의 개수<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 10-30%: 정상 범위<br/>
    • 10% 미만: 심박 리듬 규칙적 (긴장이나 피로 상태)<br/>
    • 30% 초과: 심박 변동 큼<br/><br/>
    
    <strong>해석:</strong> 부교감신경 활동과 연관된 수치. 값이 높을수록 연속 심박 간격 차이가 큼<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996
  `,
  'LF': `
    <strong>LF (Low Frequency Power)</strong><br/>
    저주파 대역 파워 (0.04-0.15 Hz) - 교감신경 활동 지표<br/><br/>
    
    <strong>측정 방법:</strong> RR간격의 Welch Periodogram을 통한 파워 스펙트럼 밀도 계산<br/>
    <strong>단위:</strong> ms² (milliseconds squared)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 200-1,200 ms²: 적절한 교감신경 활동 (연구 평균: 519±291 ms²)<br/>
    • 200 ms² 미만: 낮은 교감신경 활동 (과도한 휴식)<br/>
    • 1,200 ms² 초과: 높은 교감신경 활동 (스트레스나 긴장)<br/><br/>
    
    <strong>해석:</strong> 교감신경계의 활성도를 반영. 스트레스, 신체 활동, 정신적 각성 상태에서 증가<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'HF': `
    <strong>HF (High Frequency Power)</strong><br/>
    고주파 대역 파워 (0.15-0.4 Hz) - 부교감신경 활동 지표<br/><br/>
    
    <strong>측정 방법:</strong> RR간격의 Welch Periodogram을 통한 파워 스펙트럼 밀도 계산<br/>
    <strong>단위:</strong> ms² (milliseconds squared)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 80-4,000 ms²: 적절한 부교감신경 활동 (연구 평균: 657±777 ms²)<br/>
    • 80 ms² 미만: 낮은 부교감신경 활동 (스트레스나 피로)<br/>
    • 4,000 ms² 초과: 높은 부교감신경 활동 (깊은 휴식)<br/><br/>
    
    <strong>해석:</strong> 부교감신경계의 활성도를 반영. 휴식, 소화, 회복 상태에서 증가<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'LF/HF': `
    <strong>LF/HF Ratio</strong><br/>
    저주파/고주파 파워 비율 - 자율신경 균형<br/><br/>
    
    <strong>공식:</strong> LF/HF = LF Power / HF Power<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 1.0-10.0: 정상 범위 (연구 평균: 2.8±2.6, 권장 균형: 1.5-2.5)<br/>
    • 1.0 미만: 부교감신경 우세 (매우 편안한 상태)<br/>
    • 1.5-2.5: 이상적인 균형 상태<br/>
    • 2.5-10.0: 교감신경 우세 (활동/긴장 상태)<br/>
    • 10.0 초과: 매우 높은 LF/HF 수치<br/><br/>
    
    <strong>해석:</strong> 낮음: 휴식 상태; 중간: 균형 범위; 높음: 활동 상태<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'SDSD': `
    <strong>SDSD (Standard Deviation of Successive Differences)</strong><br/>
    심박 변화의 일관성을 나타내는 지표<br/><br/>
    
    <strong>공식:</strong> SDSD = √(Σ((RRᵢ₊₁ - RRᵢ) - mean_diff)² / (N-1))<br/>
    여기서 mean_diff는 연속 차이의 평균<br/><br/>
    
    <strong>해석:</strong><br/>
    • 15-40 ms: 정상적인 심박 변화<br/>
    • 15 ms 미만: 심박 변화 적음 (스트레스나 피로 상태)<br/>
    • 40 ms 초과: 심박 변화 활발함<br/><br/>
    
    <strong>의미:</strong> RMSSD와 비슷하지만 다른 계산법을 사용합니다. 값이 높을수록 연속 심박 간격의 변화 폭이 큽니다.<br/><br/>
    
    <strong>참고문헌:</strong> Heart Rate Variability Analysis Methods
  `,
  'PPG 스트레스': `
    <strong>설명:</strong> 심박변이도 기반 스트레스 지수로, 자율신경계의 균형 상태를 반영합니다.<br/>
    <strong>공식:</strong> 다양한 HRV 지표들의 조합을 통한 스트레스 수준 계산<br/>
    <strong>정상 범위:</strong> 30% - 70% (적절한 스트레스)<br/>
    <strong>해석:</strong> 30% 미만: 스트레스 부족, 낮은 각성; 70% 초과: 과도한 스트레스, 높은 긴장<br/>
    <strong>참고문헌:</strong> 심박변이도 기반 스트레스 측정 연구
  `,

  // ACC 지수
  'Activity State': `
    <strong>Activity State (활동 상태)</strong><br/>
    가속도계 데이터를 기반으로 분류된 신체 활동 수준<br/><br/>
    
    <strong>측정 방법:</strong> 3축 가속도(X, Y, Z)의 합성 벡터 크기를 계산하고 중력 성분(1g)을 제거<br/>
    <strong>공식:</strong> 움직임 크기 = |√(x² + y² + z²) - 1g|<br/><br/>
    
    <strong>분류 기준:</strong><br/>
    • 0.0-0.1g: Stationary (정지 상태)<br/>
    • 0.1-0.3g: Sitting (앉기/가벼운 움직임)<br/>
    • 0.3-0.8g: Walking (걷기/중간 활동)<br/>
    • 0.8g 이상: Running (뛰기/격렬한 활동)<br/><br/>
    
    <strong>해석:</strong> 가속도 크기를 기준으로 실시간 신체 활동 강도를 분류하여 표시<br/><br/>
    
    <strong>참고문헌:</strong> Troiano, R. P., et al. (2008). Medicine & Science in Sports & Exercise, 40(1), 181-188
  `,
  '안정성': `
    <strong>안정성 (Stability Index)</strong><br/>
    자세 안정성과 균형 유지 능력을 나타내는 지표<br/><br/>
    
    <strong>측정 방법:</strong> 가속도 변화의 표준편차를 이용한 안정성 계산<br/>
    <strong>공식:</strong> 안정성 = 100 - (움직임 변동성 × 정규화 계수)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 70-100%: 매우 안정적인 자세<br/>
    • 50-70%: 보통 안정성 (일상적 움직임)<br/>
    • 30-50%: 불안정한 자세<br/>
    • 30% 미만: 매우 불안정한 자세<br/><br/>
    
    <strong>해석:</strong> 값이 높을수록 가속도 변동이 작아 자세 변화가 적음을 의미<br/><br/>
    
    <strong>참고문헌:</strong> Mancini, M., & Horak, F. B. (2010). Journal of NeuroEngineering and Rehabilitation, 7(1), 17
  `,
  '강도': `
    <strong>강도 (Intensity Index)</strong><br/>
    움직임의 전반적인 강도와 에너지 소모 수준<br/><br/>
    
    <strong>측정 방법:</strong> 가속도 크기의 평균과 최대값을 이용한 강도 계산<br/>
    <strong>공식:</strong> 강도 = (평균 움직임 크기 / 최대 가능 움직임) × 100<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 0-25%: 저강도 활동 (휴식, 수면)<br/>
    • 25-50%: 중저강도 활동 (일상 생활)<br/>
    • 50-75%: 중고강도 활동 (운동, 작업)<br/>
    • 75-100%: 고강도 활동 (격렬한 운동)<br/><br/>
    
    <strong>해석:</strong> 신체 활동의 강도를 수치로 정량화하여 표시<br/><br/>
    
    <strong>참고문헌:</strong> Freedson, P. S., et al. (1998). Medicine & Science in Sports & Exercise, 30(5), 777-781
  `,
  '균형': `
    <strong>균형 (Balance Index)</strong><br/>
    X, Y축 간의 움직임 균형과 좌우/전후 대칭성<br/><br/>
    
    <strong>측정 방법:</strong> X축과 Y축 가속도의 상대적 분포와 균형도 계산<br/>
    <strong>공식:</strong> 균형 = 100 - |X축 움직임 비율 - Y축 움직임 비율| × 200<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 80-100%: 매우 균형잡힌 움직임<br/>
    • 60-80%: 양호한 균형 (정상 범위)<br/>
    • 40-60%: 낮은 균형 수치<br/>
    • 40% 미만: 매우 낮은 균형 수치<br/><br/>
    
    <strong>해석:</strong> 좌우 또는 전후 움직임의 대칭성 수치 표시<br/><br/>
    
    <strong>참고문헌:</strong> Hausdorff, J. M. (2007). Journal of NeuroEngineering and Rehabilitation, 4(1), 14
  `,
  'Average Movement': `
    <strong>Average Movement (평균 움직임)</strong><br/>
    측정 기간 동안의 평균 움직임 크기 (중력 보정)<br/><br/>
    
    <strong>측정 방법:</strong> 모든 샘플의 움직임 크기를 평균하여 계산<br/>
    <strong>공식:</strong> 평균 움직임 = Σ|√(xᵢ² + yᵢ² + zᵢ²) - 1g| / N<br/>
    <strong>단위:</strong> g (중력 가속도 단위, 1g = 9.8 m/s²)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 0.0-0.1g: 정적 상태 (수면, 휴식)<br/>
    • 0.1-0.3g: 경미한 활동 (사무, 독서)<br/>
    • 0.3-0.6g: 보통 활동 (걷기, 가사)<br/>
    • 0.6g 이상: 활발한 활동 (운동, 작업)<br/><br/>
    
    <strong>해석:</strong> 일정 기간의 전반적인 활동 수준 수치 표시<br/><br/>
    
    <strong>참고문헌:</strong> Sasaki, J. E., et al. (2011). Medicine & Science in Sports & Exercise, 43(8), 1568-1574
  `,
  'Standard Deviation Movement': `
    <strong>Standard Deviation Movement (움직임 표준편차)</strong><br/>
    움직임 크기의 변동성과 불규칙성을 나타내는 지표<br/><br/>
    
    <strong>측정 방법:</strong> 움직임 크기의 표준편차를 계산<br/>
    <strong>공식:</strong> 표준편차 = √(Σ(움직임ᵢ - 평균움직임)² / (N-1))<br/>
    <strong>단위:</strong> g (중력 가속도 단위)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 0.0-0.1g: 매우 일관된 움직임 (정적 상태)<br/>
    • 0.1-0.3g: 일관된 움직임 (규칙적 활동)<br/>
    • 0.3-0.6g: 변동적 움직임 (다양한 활동)<br/>
    • 0.6g 이상: 매우 불규칙한 움직임 (격렬하거나 불안정한 활동)<br/><br/>
    
    <strong>해석:</strong> 낮음: 규칙적이고 예측 가능한 움직임; 높음: 불규칙하고 다양한 움직임 패턴<br/><br/>
    
    <strong>참고문헌:</strong> Bussmann, J. B., & van de Berg-Emons, R. J. (2013). Gait & Posture, 37(3), 340-347
  `,
  'Max Movement': `
    <strong>Max Movement (최대 움직임)</strong><br/>
    측정 기간 중 감지된 최대 움직임 크기<br/><br/>
    
    <strong>측정 방법:</strong> 모든 샘플 중 가장 큰 움직임 크기를 추출<br/>
    <strong>공식:</strong> 최대 움직임 = max(|√(xᵢ² + yᵢ² + zᵢ²) - 1g|)<br/>
    <strong>단위:</strong> g (중력 가속도 단위)<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 0.0-0.5g: 일상적 움직임 (정상 생활)<br/>
    • 0.5-1.0g: 활발한 움직임 (운동, 작업)<br/>
    • 1.0-2.0g: 격렬한 움직임 (달리기, 점프)<br/>
    • 2.0g 이상: 매우 격렬한 움직임<br/><br/>
    
    <strong>해석:</strong> 측정 구간의 최대 가속도 수치 표시<br/><br/>
    
    <strong>참고문헌:</strong> Karantonis, D. M., et al. (2006). IEEE Transactions on Information Technology in Biomedicine, 10(1), 156-167
  `,

  // 새로운 HRV 지표들 추가
  'AVNN': `
    <strong>AVNN (Average NN Intervals)</strong><br/>
    평균 심박주기 - 심장 박동 간격의 평균<br/><br/>
    
    <strong>공식:</strong> AVNN = Σ(RRᵢ) / N<br/>
    여기서 RRᵢ는 각 NN간격, N은 총 간격 수<br/><br/>
    
    <strong>해석:</strong><br/>
    • 600-1000 ms: 안정적인 심박 리듬<br/>
    • 600 ms 미만: 빠른 심박 (활동적이거나 긴장 상태)<br/>
    • 1000 ms 초과: 느린 심박 (휴식 상태나 운동선수형)<br/><br/>
    
    <strong>의미:</strong> 심박수가 빠를수록 AVNN은 작아지고, 느릴수록 커집니다. 개인의 기본 심박 상태를 나타내는 중요한 지표입니다.<br/><br/>
    
    <strong>참고문헌:</strong> Task Force of ESC/NASPE, 1996
  `,
  'PNN20': `
    <strong>PNN20 (Percentage of NN20)</strong><br/>
    심박 리듬의 미세한 변화 감지 지표<br/><br/>
    
    <strong>공식:</strong> PNN20 = (NN20 count / Total NN intervals) × 100%<br/>
    NN20 = |RRᵢ₊₁ - RRᵢ| > 20ms인 간격의 개수<br/><br/>
    
    <strong>해석:</strong><br/>
    • 20-60%: 적절한 심박 변화<br/>
    • 20% 미만: 심박 리듬 일정함 (긴장이나 피로 상태)<br/>
    • 60% 초과: 심박 변동 큼<br/><br/>
    
    <strong>의미:</strong> PNN50보다 민감하여 작은 변화도 감지합니다. 값이 높을수록 연속 심박 간격의 변화 폭이 큽니다.<br/><br/>
    
    <strong>참고문헌:</strong> HRV Analysis Methods, IEEE TBME
  `,
  'HR Max': `
    <strong>HR Max (Heart Rate Maximum)</strong><br/>
    최근 2분간(120개 샘플) 측정된 심박수 중 최고점<br/><br/>
    
    <strong>계산 방법:</strong> Moving Average Queue(120개)에서 BPM 최대값 추출<br/>
    • 각 시점의 심박수를 BPM으로 변환<br/>
    • 고품질 데이터만 큐에 저장<br/>
    • 큐에서 최고점을 실시간으로 추적<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 80-150 BPM: 정상 최대 심박수<br/>
    • 80 BPM 미만: 낮은 최대 심박수<br/>
    • 150 BPM 초과: 높은 최대 심박수<br/><br/>
    
    <strong>해석:</strong> 최근 2분간 심박수 변동의 상한선 수치<br/><br/>
    
    <strong>참고문헌:</strong> Heart Rate Variability Analysis Guidelines
  `,
  'HR Min': `
    <strong>HR Min (Heart Rate Minimum)</strong><br/>
    최근 2분간(120개 샘플) 측정된 심박수 중 최저점<br/><br/>
    
    <strong>계산 방법:</strong> Moving Average Queue(120개)에서 BPM 최소값 추출<br/>
    • 각 시점의 심박수를 BPM으로 변환<br/>
    • 고품질 데이터만 큐에 저장<br/>
    • 큐에서 최저점을 실시간으로 추적<br/><br/>
    
    <strong>정상 범위:</strong><br/>
    • 50-80 BPM: 정상 최소 심박수<br/>
    • 50 BPM 미만: 낮은 최소 심박수<br/>
    • 80 BPM 초과: 높은 최소 심박수<br/><br/>
    
    <strong>해석:</strong> 최근 2분간 심박수 변동의 하한선 수치<br/><br/>
    
    <strong>참고문헌:</strong> Heart Rate Variability Analysis Guidelines
  `
};

export const indexGuidesEN: Record<string, string> = {
  // EEG Indices
  '집중력': `
    <strong>Description:</strong> Focus Index measures cognitive concentration levels, calculated as the ratio of beta power to the sum of alpha and theta power. Higher values indicate deeper focus, lower values indicate distraction.<br/>
    <strong>Formula:</strong> Focus Index = Beta Power / (Alpha Power + Theta Power)<br/>
    <strong>Normal Range:</strong> 1.8 - 2.4<br/>
    <strong>Interpretation:</strong><br/>
    • 1.8 - 2.4: Normal concentration level<br/>
    • Below 1.8: Attention deficit or drowsiness<br/>
    • Above 2.4: Excessive focus or stress<br/>
    <strong>References:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance. Brain Research Reviews, 29(2-3), 169-195
  `,
  '이완도': `
    <strong>Description:</strong> Relaxation Index measures mental relaxation based on relative alpha activity. Higher values indicate a more relaxed state.<br/>
    <strong>Formula:</strong> Relaxation Index = Alpha Power / (Alpha Power + Beta Power)<br/>
    <strong>Normal Range:</strong> 0.18 - 0.22 (normal tension state)<br/>
    <strong>Interpretation:</strong> Below 0.18: Tension and stress; Above 0.22: Excessive relaxation<br/>
    <strong>References:</strong> Bazanova, O. M., & Vernon, D. (2014). Neuroscience & Biobehavioral Reviews, 44, 94-110
  `,
  '스트레스': `
    <strong>Description:</strong> Stress Index represents mental stress and arousal, rising with increased high-frequency (beta, gamma) activity.<br/>
    <strong>Formula:</strong> Stress Index = (Beta Power + Gamma Power) / (Alpha Power + Theta Power)<br/>
    <strong>Normal Range:</strong> 3.0 - 4.0 (normal range)<br/>
    <strong>Interpretation:</strong> Below 2.0: Very low stress; 2.0-3.0: Low stress; 3.0-4.0: Normal; 4.0-5.0: High stress; Above 5.0: Very high stress value<br/>
    <strong>References:</strong> Ahn, J. W., et al. (2019). Sensors, 19(21), 4644
  `,
  '좌우뇌 균형': `
    <strong>Description:</strong> Hemispheric Balance represents the balance of alpha activity between left and right hemispheres, reflecting emotional and cognitive bias.<br/>
    <strong>Formula:</strong> (Left Alpha - Right Alpha) / (Left Alpha + Right Alpha)<br/>
    <strong>Normal Range:</strong> -0.1 to 0.1 (balanced state)<br/>
    <strong>Interpretation:</strong> Below -0.1: Creative (right brain dominant); Above 0.1: Logical (left brain dominant)<br/>
    <strong>References:</strong> Davidson, R. J. (2004). Biological Psychology, 67(1-2), 219-234
  `,
  '인지 부하': `
    <strong>Description:</strong> Cognitive Load reflects mental workload and effort based on theta/alpha ratio.<br/>
    <strong>Formula:</strong> Cognitive Load = Theta Power / Alpha Power<br/>
    <strong>Normal Range:</strong> 0.3 - 0.8 (optimal load)<br/>
    <strong>Interpretation:</strong> Below 0.3: Low engagement; Above 0.8: High cognitive load; Above 1.2: Overload<br/>
    <strong>References:</strong> Gevins, A., & Smith, M. E. (2003). Theoretical Issues in Ergonomics Science, 4(1-2), 113-131
  `,
  '정서 안정성': `
    <strong>Description:</strong> Emotional Stability measures emotion regulation ability based on the ratio of low-frequency to gamma power.<br/>
    <strong>Formula:</strong> Emotional Stability = (Alpha Power + Theta Power) / Gamma Power<br/>
    <strong>Normal Range:</strong> 0.4 - 0.8 (normal range)<br/>
    <strong>Interpretation:</strong> Below 0.4: Emotional instability, excessive arousal; Above 0.8: Emotional blunting, excessive inhibition<br/>
    <strong>References:</strong> Knyazev, G. G. (2007). Neuroscience & Biobehavioral Reviews, 31(3), 377-395
  `,
  '총 파워': `
    <strong>Neural Activity</strong><br/>
    <strong>Description:</strong> Sum of all EEG band powers, representing overall neural activity level.<br/>
    <strong>Formula:</strong> Delta + Theta + Alpha + Beta + Gamma band power sum<br/>
    <strong>Normal Range:</strong> 850-1150 μV²<br/>
    <strong>Interpretation:</strong><br/>
    • 850-1150: Normal neural activity<br/>
    • Above 1150: Excessive neural activity (hyperarousal, stress)<br/>
    • Below 850: Suppressed neural activity (hypoarousal, drowsiness)<br/>
    <strong>References:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance
  `,
  '신경활동성': `
    <strong>Neural Activity</strong><br/>
    <strong>Description:</strong> Sum of all EEG band powers, representing overall neural activity level.<br/>
    <strong>Formula:</strong> Delta + Theta + Alpha + Beta + Gamma band power sum<br/>
    <strong>Normal Range:</strong> 850-1150 μV²<br/>
    <strong>Interpretation:</strong><br/>
    • 850-1150: Normal neural activity<br/>
    • Above 1150: Excessive neural activity (hyperarousal, stress)<br/>
    • Below 850: Suppressed neural activity (hypoarousal, drowsiness)<br/>
    <strong>References:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance
  `,
  '집중력 (Focus)': `
    <strong>Description:</strong> Focus Index measures cognitive concentration levels, calculated as the ratio of beta power to the sum of alpha and theta power. Higher values indicate deeper focus.<br/>
    <strong>Formula:</strong> Focus Index = Beta Power / (Alpha Power + Theta Power)<br/>
    <strong>Normal Range:</strong> 1.8 - 2.4<br/>
    <strong>Interpretation:</strong><br/>
    • 1.8 - 2.4: Normal concentration<br/>
    • Below 1.8: Attention deficit or drowsiness<br/>
    • Above 2.4: Excessive focus or stress<br/>
    <strong>References:</strong> Klimesch, W. (1999). EEG alpha and theta oscillations reflect cognitive and memory performance. Brain Research Reviews, 29(2-3), 169-195
  `,
  '이완및긴장도 (Arousal)': `
    <strong>Description:</strong> Arousal Index measures mental alertness and relaxation based on relative alpha activity. Higher values indicate a more relaxed state, lower values indicate tension.<br/>
    <strong>Formula:</strong> Arousal Index = Alpha Power / (Alpha Power + Beta Power)<br/>
    <strong>Normal Range:</strong> 0.18 - 0.22 (normal tension state)<br/>
    <strong>Interpretation:</strong> Below 0.18: Tension and stress; Above 0.22: Excessive relaxation<br/>
    <strong>References:</strong> Bazanova, O. M., & Vernon, D. (2014). Neuroscience & Biobehavioral Reviews, 44, 94-110
  `,
  '정서안정성 (Valence)': `
    <strong>Description:</strong> Emotional Stability Index measures emotion regulation and emotional stability based on the ratio of low-frequency to gamma power. Values within proper range indicate stable emotional state.<br/>
    <strong>Formula:</strong> Emotional Stability = (Alpha Power + Theta Power) / Gamma Power<br/>
    <strong>Normal Range:</strong> 0.4 - 0.8 (normal range)<br/>
    <strong>Interpretation:</strong> Below 0.4: Emotional instability, excessive arousal; Above 0.8: Emotional blunting, excessive inhibition<br/>
    <strong>References:</strong> Knyazev, G. G. (2007). Neuroscience & Biobehavioral Reviews, 31(3), 377-395
  `,
  '스트레스 (Stress)': `
    <strong>Description:</strong> Stress Index represents mental stress and arousal, rising with increased high-frequency (beta, gamma) activity. Higher values indicate higher stress levels.<br/>
    <strong>Formula:</strong> Stress Index = (Beta Power + Gamma Power) / (Alpha Power + Theta Power)<br/>
    <strong>Normal Range:</strong> 3.0 - 4.0 (normal range)<br/>
    <strong>Interpretation:</strong> Below 2.0: Very low stress; 2.0-3.0: Low stress; 3.0-4.0: Normal; 4.0-5.0: High stress; Above 5.0: Very high stress value<br/>
    <strong>References:</strong> Ahn, J. W., et al. (2019). Sensors, 19(21), 4644
  `,
  // PPG Indices
  'BPM': `
    <strong>BPM (Beats Per Minute)</strong><br/>
    Heart rate - number of heartbeats per minute<br/><br/>
    <strong>Measurement:</strong> Calculated by analyzing peak intervals from PPG signal<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 60-100 BPM: Normal range<br/>
    • Below 60 BPM: Low heart rate<br/>
    • Above 100 BPM: High heart rate<br/><br/>
    <strong>Interpretation:</strong> Number of heartbeats per minute. Varies with exercise, posture, and activity<br/><br/>
    <strong>References:</strong> American Heart Association Guidelines
  `,
  'SpO2': `
    <strong>SpO2 (Oxygen Saturation)</strong><br/>
    Blood oxygen saturation level<br/><br/>
    <strong>Measurement:</strong> Calculated using Beer-Lambert law based on Red/IR light absorption ratio<br/>
    • R = (Red_AC/Red_DC) / (IR_AC/IR_DC)<br/>
    • Multi-stage calibration formula for SpO2 estimation<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 98-100%: Normal oxygen saturation<br/>
    • 95-98%: Normal range (lower bound)<br/>
    • 90-95%: Slightly low oxygen saturation<br/>
    • Below 90%: Low oxygen saturation (measured value)<br/><br/>
    <strong>Interpretation:</strong> Displays oxygen saturation estimated by optical sensor. Not a medical device; accuracy is limited.<br/><br/>
    <strong>References:</strong> Pulse Oximetry Principles, IEEE TBME
  `,
  'Stress': `
    <strong>Stress Index</strong><br/>
    HRV-based normalized stress level (0.0-1.0)<br/><br/>
    <strong>Formula:</strong> Weighted average of normalized SDNN, RMSSD, and heart rate<br/>
    • Stress = (normSDNN × 0.4) + (normRMSSD × 0.4) + (HRstress × 0.2)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 0.30-0.70: Normal range (balanced state)<br/>
    • 0.00-0.30: Very low stress (excessive relaxation)<br/>
    • 0.70-0.90: High stress (tension state)<br/>
    • 0.90-1.00: Very high stress value<br/><br/>
    <strong>Interpretation:</strong> Low: low stress value; High: high stress value<br/><br/>
    <strong>References:</strong> HRV Analysis Methods, Frontiers in Physiology
  `,
  'SDNN': `
    <strong>SDNN (Standard Deviation of NN intervals)</strong><br/>
    Standard deviation of NN intervals - overall HRV level<br/><br/>
    <strong>Formula:</strong> SDNN = √(Σ(RRᵢ - RR̄)² / (N-1))<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 30-100 ms: Normal range<br/>
    • Below 30 ms: Regular heart rhythm (stress or fatigue)<br/>
    • Above 100 ms: High heart rate variability<br/><br/>
    <strong>Interpretation:</strong> Low: small beat-to-beat variation; High: large beat-to-beat variation<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996
  `,
  'RMSSD': `
    <strong>RMSSD (Root Mean Square of Successive Differences)</strong><br/>
    Root mean square of successive RR interval differences<br/><br/>
    <strong>Formula:</strong> RMSSD = √(Σ(RRᵢ₊₁ - RRᵢ)² / (N-1))<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 20-50 ms: Normal range<br/>
    • Below 20 ms: Tension state (rest needed)<br/>
    • Above 50 ms: Very relaxed state (deep rest)<br/><br/>
    <strong>Interpretation:</strong> Low: tension (insufficient rest); High: very relaxed state<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996
  `,
  'PNN50': `
    <strong>PNN50 (Percentage of NN50)</strong><br/>
    Percentage of consecutive NN intervals differing by more than 50ms<br/><br/>
    <strong>Formula:</strong> PNN50 = (NN50 count / Total NN intervals) × 100%<br/>
    NN50 = count of intervals where |RRᵢ₊₁ - RRᵢ| > 50ms<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 10-30%: Normal range<br/>
    • Below 10%: Regular rhythm (tension or fatigue)<br/>
    • Above 30%: High heart rate variability<br/><br/>
    <strong>Interpretation:</strong> Metric associated with parasympathetic activity. Higher values mean larger successive interval differences.<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996
  `,
  'LF': `
    <strong>LF (Low Frequency Power)</strong><br/>
    Low frequency band power (0.04-0.15 Hz) - sympathetic activity indicator<br/><br/>
    <strong>Measurement:</strong> Power spectral density via Welch Periodogram of RR intervals<br/>
    <strong>Unit:</strong> ms² (milliseconds squared)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 200-1,200 ms²: Appropriate sympathetic activity (study avg: 519±291 ms²)<br/>
    • Below 200 ms²: Low sympathetic activity (excessive rest)<br/>
    • Above 1,200 ms²: High sympathetic activity (stress or tension)<br/><br/>
    <strong>Interpretation:</strong> Reflects sympathetic nervous system activation. Increases with stress, physical activity, mental arousal.<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'HF': `
    <strong>HF (High Frequency Power)</strong><br/>
    High frequency band power (0.15-0.4 Hz) - parasympathetic activity indicator<br/><br/>
    <strong>Measurement:</strong> Power spectral density via Welch Periodogram of RR intervals<br/>
    <strong>Unit:</strong> ms² (milliseconds squared)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 80-4,000 ms²: Appropriate parasympathetic activity (study avg: 657±777 ms²)<br/>
    • Below 80 ms²: Low parasympathetic activity (stress or fatigue)<br/>
    • Above 4,000 ms²: High parasympathetic activity (deep rest)<br/><br/>
    <strong>Interpretation:</strong> Reflects parasympathetic nervous system activation. Increases during rest, digestion, recovery.<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'LF/HF': `
    <strong>LF/HF Ratio</strong><br/>
    Low frequency / high frequency power ratio - autonomic balance<br/><br/>
    <strong>Formula:</strong> LF/HF = LF Power / HF Power<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 1.0-10.0: Normal range (study avg: 2.8±2.6, ideal balance: 1.5-2.5)<br/>
    • Below 1.0: Parasympathetic dominant (very relaxed)<br/>
    • 1.5-2.5: Ideal balanced state<br/>
    • 2.5-10.0: Sympathetic dominant (active/tense)<br/>
    • Above 10.0: Very high LF/HF value<br/><br/>
    <strong>Interpretation:</strong> Low: resting state; Mid: balanced range; High: active state<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996; Shaffer & Ginsberg, 2017
  `,
  'SDSD': `
    <strong>SDSD (Standard Deviation of Successive Differences)</strong><br/>
    Indicator of heart rate variability consistency<br/><br/>
    <strong>Formula:</strong> SDSD = √(Σ((RRᵢ₊₁ - RRᵢ) - mean_diff)² / (N-1))<br/><br/>
    <strong>Interpretation:</strong><br/>
    • 15-40 ms: Normal HR variation<br/>
    • Below 15 ms: Low HR variation (stress or fatigue)<br/>
    • Above 40 ms: Active HR variation<br/><br/>
    <strong>Meaning:</strong> Similar to RMSSD but uses a different calculation. Higher values mean larger successive interval differences.<br/><br/>
    <strong>References:</strong> Heart Rate Variability Analysis Methods
  `,
  'PPG 스트레스': `
    <strong>Description:</strong> HRV-based stress index reflecting autonomic nervous system balance.<br/>
    <strong>Formula:</strong> Stress level calculated from combination of various HRV metrics<br/>
    <strong>Normal Range:</strong> 30% - 70%<br/>
    <strong>Interpretation:</strong> Below 30%: Low stress, low arousal; Above 70%: Excessive stress, high tension<br/>
    <strong>References:</strong> HRV-based stress measurement research
  `,
  // ACC Indices
  'Activity State': `
    <strong>Activity State</strong><br/>
    Physical activity level classified from accelerometer data<br/><br/>
    <strong>Measurement:</strong> Calculate composite vector magnitude from 3-axis acceleration (X, Y, Z) and remove gravity component (1g)<br/>
    <strong>Formula:</strong> Movement magnitude = |√(x² + y² + z²) - 1g|<br/><br/>
    <strong>Classification:</strong><br/>
    • 0.0-0.1g: Stationary<br/>
    • 0.1-0.3g: Sitting (light movement)<br/>
    • 0.3-0.8g: Walking (moderate activity)<br/>
    • Above 0.8g: Running (vigorous activity)<br/><br/>
    <strong>Interpretation:</strong> Classifies real-time physical activity intensity from acceleration magnitude<br/><br/>
    <strong>References:</strong> Troiano, R. P., et al. (2008). Medicine & Science in Sports & Exercise, 40(1), 181-188
  `,
  '안정성': `
    <strong>Stability Index</strong><br/>
    Indicator of postural stability and balance maintenance<br/><br/>
    <strong>Measurement:</strong> Stability calculation using standard deviation of acceleration changes<br/>
    <strong>Formula:</strong> Stability = 100 - (movement variability × normalization factor)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 70-100%: Very stable posture<br/>
    • 50-70%: Moderate stability (daily movement)<br/>
    • 30-50%: Unstable posture<br/>
    • Below 30%: Very unstable posture<br/><br/>
    <strong>Interpretation:</strong> Higher values indicate smaller acceleration variability, meaning less postural change<br/><br/>
    <strong>References:</strong> Mancini, M., & Horak, F. B. (2010). Journal of NeuroEngineering and Rehabilitation, 7(1), 17
  `,
  '강도': `
    <strong>Intensity Index</strong><br/>
    Overall movement intensity and energy expenditure level<br/><br/>
    <strong>Measurement:</strong> Intensity calculation using average and maximum acceleration magnitude<br/>
    <strong>Formula:</strong> Intensity = (avg movement / max possible) × 100<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 0-25%: Low intensity (rest, sleep)<br/>
    • 25-50%: Medium-low intensity (daily life)<br/>
    • 50-75%: Medium-high intensity (exercise, work)<br/>
    • 75-100%: High intensity (intense exercise)<br/><br/>
    <strong>Interpretation:</strong> Quantifies physical activity intensity as a numeric value<br/><br/>
    <strong>References:</strong> Freedson, P. S., et al. (1998). Medicine & Science in Sports & Exercise, 30(5), 777-781
  `,
  '균형': `
    <strong>Balance Index</strong><br/>
    Movement balance between X and Y axes and left-right / front-back symmetry<br/><br/>
    <strong>Measurement:</strong> Relative distribution and balance of X and Y axis acceleration<br/>
    <strong>Formula:</strong> Balance = 100 - |X-axis ratio - Y-axis ratio| × 200<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 80-100%: Very balanced movement<br/>
    • 60-80%: Good balance (normal range)<br/>
    • 40-60%: Low balance value<br/>
    • Below 40%: Very low balance value<br/><br/>
    <strong>Interpretation:</strong> Displays symmetry of left-right or front-back movement<br/><br/>
    <strong>References:</strong> Hausdorff, J. M. (2007). Journal of NeuroEngineering and Rehabilitation, 4(1), 14
  `,
  'Average Movement': `
    <strong>Average Movement</strong><br/>
    Average movement magnitude during measurement period (gravity corrected)<br/><br/>
    <strong>Measurement:</strong> Average of all sample movement magnitudes<br/>
    <strong>Formula:</strong> Avg Movement = Σ|√(xᵢ² + yᵢ² + zᵢ²) - 1g| / N<br/>
    <strong>Unit:</strong> g (gravitational acceleration, 1g = 9.8 m/s²)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 0.0-0.1g: Static state (sleep, rest)<br/>
    • 0.1-0.3g: Light activity (desk work, reading)<br/>
    • 0.3-0.6g: Moderate activity (walking, housework)<br/>
    • Above 0.6g: Active (exercise, labor)<br/><br/>
    <strong>Interpretation:</strong> Displays overall activity level over the measurement period<br/><br/>
    <strong>References:</strong> Sasaki, J. E., et al. (2011). Medicine & Science in Sports & Exercise, 43(8), 1568-1574
  `,
  'Standard Deviation Movement': `
    <strong>Standard Deviation Movement</strong><br/>
    Indicator of movement variability and irregularity<br/><br/>
    <strong>Measurement:</strong> Standard deviation of movement magnitudes<br/>
    <strong>Formula:</strong> StdDev = √(Σ(movementᵢ - avg)² / (N-1))<br/>
    <strong>Unit:</strong> g (gravitational acceleration)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 0.0-0.1g: Very consistent movement (static)<br/>
    • 0.1-0.3g: Consistent movement (regular activity)<br/>
    • 0.3-0.6g: Variable movement (diverse activities)<br/>
    • Above 0.6g: Very irregular movement (intense or unstable)<br/><br/>
    <strong>Interpretation:</strong> Low: regular, predictable movement; High: irregular, diverse movement patterns<br/><br/>
    <strong>References:</strong> Bussmann, J. B., & van de Berg-Emons, R. J. (2013). Gait & Posture, 37(3), 340-347
  `,
  'Max Movement': `
    <strong>Max Movement</strong><br/>
    Maximum movement magnitude detected during measurement<br/><br/>
    <strong>Measurement:</strong> Extract the largest movement magnitude from all samples<br/>
    <strong>Formula:</strong> Max Movement = max(|√(xᵢ² + yᵢ² + zᵢ²) - 1g|)<br/>
    <strong>Unit:</strong> g (gravitational acceleration)<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 0.0-0.5g: Daily movement (normal life)<br/>
    • 0.5-1.0g: Active movement (exercise, labor)<br/>
    • 1.0-2.0g: Vigorous movement (running, jumping)<br/>
    • Above 2.0g: Very intense movement<br/><br/>
    <strong>Interpretation:</strong> Displays the peak acceleration value of the measurement window<br/><br/>
    <strong>References:</strong> Karantonis, D. M., et al. (2006). IEEE Transactions on Information Technology in Biomedicine, 10(1), 156-167
  `,
  // New HRV Metrics
  'AVNN': `
    <strong>AVNN (Average NN Intervals)</strong><br/>
    Average cardiac cycle duration - mean heartbeat interval<br/><br/>
    <strong>Formula:</strong> AVNN = Σ(RRᵢ) / N<br/><br/>
    <strong>Interpretation:</strong><br/>
    • 600-1000 ms: Stable heart rhythm<br/>
    • Below 600 ms: Fast heartbeat (active or tense)<br/>
    • Above 1000 ms: Slow heartbeat (resting or athletic)<br/><br/>
    <strong>Meaning:</strong> Smaller AVNN with faster heart rate, larger with slower. Baseline measured value.<br/><br/>
    <strong>References:</strong> Task Force of ESC/NASPE, 1996
  `,
  'PNN20': `
    <strong>PNN20 (Percentage of NN20)</strong><br/>
    Sensitive detector of subtle heart rhythm changes<br/><br/>
    <strong>Formula:</strong> PNN20 = (NN20 count / Total NN intervals) × 100%<br/>
    NN20 = count of intervals where |RRᵢ₊₁ - RRᵢ| > 20ms<br/><br/>
    <strong>Interpretation:</strong><br/>
    • 20-60%: Appropriate HR variability<br/>
    • Below 20%: Regular rhythm (tension or fatigue)<br/>
    • Above 60%: High heart rate variability<br/><br/>
    <strong>Meaning:</strong> More sensitive than PNN50, detects even minor changes. Higher values mean larger successive interval differences.<br/><br/>
    <strong>References:</strong> HRV Analysis Methods, IEEE TBME
  `,
  'HR Max': `
    <strong>HR Max (Heart Rate Maximum)</strong><br/>
    Peak heart rate measured within the last 2 minutes (120 samples)<br/><br/>
    <strong>Calculation:</strong> Extract BPM maximum from Moving Average Queue (120)<br/>
    • Convert heart rate at each time point to BPM<br/>
    • Store only high-quality data in queue<br/>
    • Track peak in real-time<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 80-150 BPM: Normal max heart rate<br/>
    • Below 80 BPM: Low max heart rate<br/>
    • Above 150 BPM: High max heart rate<br/><br/>
    <strong>Interpretation:</strong> Upper bound of heart rate variation in the last 2 minutes<br/><br/>
    <strong>References:</strong> Heart Rate Variability Analysis Guidelines
  `,
  'HR Min': `
    <strong>HR Min (Heart Rate Minimum)</strong><br/>
    Lowest heart rate measured within the last 2 minutes (120 samples)<br/><br/>
    <strong>Calculation:</strong> Extract BPM minimum from Moving Average Queue (120)<br/>
    • Convert heart rate at each time point to BPM<br/>
    • Store only high-quality data in queue<br/>
    • Track minimum in real-time<br/><br/>
    <strong>Normal Range:</strong><br/>
    • 50-80 BPM: Normal min heart rate<br/>
    • Below 50 BPM: Low min heart rate<br/>
    • Above 80 BPM: High min heart rate<br/><br/>
    <strong>Interpretation:</strong> Lower bound of heart rate variation in the last 2 minutes<br/><br/>
    <strong>References:</strong> Heart Rate Variability Analysis Guidelines
  `,
};

/**
 * 지표 라벨에 대응하는 가이드 HTML을 반환한다.
 *
 * 반환값 끝에는 항상 비의료기기 면책 문구를 덧붙인다 — 지표 설명이
 * 임상 진단·권고로 오인되지 않도록 하는 정책 요구사항.
 */
export function getIndexGuide(label: string, lang: 'ko' | 'en' = 'ko'): string {
  const guides = lang === 'en' ? indexGuidesEN : indexGuidesKO;
  const disclaimer = lang === 'en' ? INDEX_GUIDE_DISCLAIMER_EN : INDEX_GUIDE_DISCLAIMER_KO;
  const body =
    guides[label] ||
    `<strong>${label}</strong><br/>${lang === 'en' ? 'Detailed information is being prepared.' : '상세 정보가 준비 중입니다.'}`;
  return `${body}<br/><br/>${disclaimer}`;
}
